import type { RFQ, RFQItem, RFQResponse, RFQSupplier } from '../types';
import { dataService } from './dataService';

// ============================================================================
// WARNING: PERSISTENCIA CON localStorage - PARA DEMO SOLAMENTE
// ============================================================================
// Este módulo usa localStorage para persistir RFQs entre recargas.
// ESTO NO ES ADECUADO PARA PRODUCCIÓN por:
//   - Limitado a 5-10MB por dominio
//   - No sincroniza entre usuarios/dispositivos
//   - No hay validación server-side
//   - No hay backup automático
//   - Datos se pierden si el usuario limpia el navegador
//
// EN PRODUCCIÓN, se necesita implementar:
//   1. POST   /api/rfqs                     - Crear nueva compulsa
//   2. GET    /api/rfqs                     - Listar todas las compulsas
//   3. GET    /api/rfqs/:id                 - Detalle de una compulsa
//   4. PUT    /api/rfqs/:id                 - Actualizar compulsa
//   5. PATCH  /api/rfqs/:id/send            - Enviar compulsa (trigger emails reales)
//   6. PATCH  /api/rfqs/:id/close           - Cerrar compulsa
//   7. POST   /api/rfqs/:id/responses       - Registrar respuesta de proveedor
//   8. DELETE /api/rfqs/:id                 - Eliminar compulsa (solo borrador)
//
// Consideraciones adicionales:
//   - Timestamps deben generarse en el servidor
//   - El envío de email debe ser real (Microsoft Graph API o SMTP)
//   - Validar permisos de usuario
//   - Implementar auditoría de cambios
//   - Notificaciones automáticas (deadline, respuestas)
//   - Integración con módulo de evaluación de proveedores
//
// ⚠️ NO USAR EN PRODUCCIÓN SIN MIGRAR A BACKEND CORPORATIVO
// ============================================================================

const STORAGE_KEY = 'interbarge_rfqs';

class RFQService {
  // =========================================================================
  // CRUD Operations
  // =========================================================================

  getAll(): RFQ[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];
      return JSON.parse(stored) as RFQ[];
    } catch (error) {
      console.error('[RFQService] Error cargando RFQs:', error);
      return [];
    }
  }

  getById(id: string): RFQ | null {
    const rfqs = this.getAll();
    return rfqs.find(r => r.id === id) || null;
  }

  create(data: {
    title: string;
    items: RFQItem[];
    suppliers: RFQSupplier[];
    deadline: string;
    notes?: string;
    mainCategory: string;
    createdBy?: string;
  }): RFQ {
    const rfqs = this.getAll();

    const newRFQ: RFQ = {
      id: crypto.randomUUID(),
      number: this.getNextNumber(),
      title: data.title,
      status: 'draft',
      items: data.items,
      suppliers: data.suppliers,
      responses: [],
      createdBy: data.createdBy || 'Demo User',
      createdAt: new Date().toISOString(),
      deadline: data.deadline,
      notes: data.notes,
      mainCategory: data.mainCategory,
    };

    rfqs.push(newRFQ);
    this.saveAll(rfqs);
    console.log(`✅ [RFQService] RFQ creado: ${newRFQ.number} - ${newRFQ.title}`);
    return newRFQ;
  }

  update(id: string, data: Partial<RFQ>): RFQ {
    const rfqs = this.getAll();
    const index = rfqs.findIndex(r => r.id === id);
    if (index === -1) throw new Error(`RFQ ${id} no encontrado`);

    rfqs[index] = { ...rfqs[index], ...data };
    this.saveAll(rfqs);
    console.log(`✅ [RFQService] RFQ actualizado: ${rfqs[index].number}`);
    return rfqs[index];
  }

  delete(id: string): void {
    const rfqs = this.getAll();
    const filtered = rfqs.filter(r => r.id !== id);
    if (filtered.length === rfqs.length) throw new Error(`RFQ ${id} no encontrado`);
    this.saveAll(filtered);
    console.log(`✅ [RFQService] RFQ eliminado: ${id}`);
  }

  send(id: string): RFQ {
    const rfqs = this.getAll();
    const index = rfqs.findIndex(r => r.id === id);
    if (index === -1) throw new Error(`RFQ ${id} no encontrado`);

    const now = new Date().toISOString();
    rfqs[index].status = 'sent';
    rfqs[index].sentAt = now;
    rfqs[index].suppliers = rfqs[index].suppliers.map(s => ({
      ...s,
      sentAt: now,
    }));

    this.saveAll(rfqs);
    console.log(`✅ [RFQService] RFQ enviado: ${rfqs[index].number} a ${rfqs[index].suppliers.length} proveedores`);
    return rfqs[index];
  }

  close(id: string): RFQ {
    const rfqs = this.getAll();
    const index = rfqs.findIndex(r => r.id === id);
    if (index === -1) throw new Error(`RFQ ${id} no encontrado`);

    rfqs[index].status = 'closed';
    rfqs[index].closedAt = new Date().toISOString();

    this.saveAll(rfqs);
    console.log(`✅ [RFQService] RFQ cerrado: ${rfqs[index].number}`);
    return rfqs[index];
  }

  addResponse(rfqId: string, response: Omit<RFQResponse, 'id' | 'submittedAt'>): RFQ {
    const rfqs = this.getAll();
    const index = rfqs.findIndex(r => r.id === rfqId);
    if (index === -1) throw new Error(`RFQ ${rfqId} no encontrado`);

    const fullResponse: RFQResponse = {
      ...response,
      id: crypto.randomUUID(),
      submittedAt: new Date().toISOString(),
    };

    rfqs[index].responses.push(fullResponse);

    // Marcar proveedor como respondido
    const supplierIndex = rfqs[index].suppliers.findIndex(
      s => s.supplierId === response.supplierId
    );
    if (supplierIndex !== -1) {
      rfqs[index].suppliers[supplierIndex].hasResponded = true;
      rfqs[index].suppliers[supplierIndex].respondedAt = new Date().toISOString();
    }

    // Si todos los proveedores respondieron, cambiar status
    const allResponded = rfqs[index].suppliers.every(s => s.hasResponded);
    if (allResponded) {
      rfqs[index].status = 'responses';
    }

    this.saveAll(rfqs);
    console.log(`✅ [RFQService] Respuesta agregada al RFQ ${rfqs[index].number} de ${response.supplierName}`);
    return rfqs[index];
  }

  // =========================================================================
  // Helpers
  // =========================================================================

  getNextNumber(): string {
    const rfqs = this.getAll();
    const maxNum = rfqs.reduce((max, rfq) => {
      const num = parseInt(rfq.number.replace('RFQ-', ''), 10);
      return isNaN(num) ? max : Math.max(max, num);
    }, 0);
    return `RFQ-${String(maxNum + 1).padStart(3, '0')}`;
  }

  private saveAll(rfqs: RFQ[]): void {
    try {
      const jsonString = JSON.stringify(rfqs);
      localStorage.setItem(STORAGE_KEY, jsonString);
      console.log(`✅ [RFQService] Guardado exitoso:`, {
        key: STORAGE_KEY,
        totalRFQs: rfqs.length,
        sizeKB: (jsonString.length / 1024).toFixed(2),
      });

      // Disparar evento custom para notificar a otros componentes (Dashboard, etc.)
      window.dispatchEvent(new Event('rfqsUpdated'));
    } catch (error) {
      console.error('❌ [RFQService] ERROR al guardar RFQs:', error);
    }
  }

  // =========================================================================
  // Inicialización con mock data
  // =========================================================================

  initializeIfEmpty(): void {
    const existing = this.getAll();
    if (existing.length > 0) {
      console.log(`[RFQService] Ya hay ${existing.length} RFQs en localStorage`);
      return;
    }

    console.log('[RFQService] localStorage vacío, generando mock data...');
    const mockRFQs = this.generateMockRFQs();
    this.saveAll(mockRFQs);
    console.log(`✅ [RFQService] Mock data generado: ${mockRFQs.length} RFQs`);
  }

  private generateMockRFQs(): RFQ[] {
    // Obtener proveedores y categorías reales del CSV
    const allSuppliers = dataService.getSuppliers();
    const allCategories = dataService.getCategories();

    // Helper para generar email mock
    const mockEmail = (supplier: string): string => {
      const clean = supplier.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 15);
      return `compras@${clean}.com`;
    };

    // Helper para generar RFQSupplier
    const makeSupplier = (name: string, sent: boolean, responded: boolean): RFQSupplier => ({
      supplierId: `supplier-${name.toLowerCase().replace(/\s+/g, '-')}`,
      supplierName: name,
      contactEmail: mockEmail(name),
      sentAt: sent ? '2026-03-15T10:00:00Z' : undefined,
      respondedAt: responded ? '2026-03-20T14:30:00Z' : undefined,
      hasResponded: responded,
    });

    // Elegir proveedores y categorías reales (si hay datos cargados del CSV)
    const supplierNames = allSuppliers.length > 10
      ? allSuppliers.slice(0, 20)
      : ['Movicor SRL', 'Marine Parts Co', 'Navales del Sur', 'Hidráulica Litoral', 'Motores BAI', 'Delta Industrial', 'Río Supply', 'Astilleros del Este', 'Patagonia Marine', 'Puerto Seguro'];

    const categoryNames = allCategories.length > 10
      ? allCategories.slice(0, 15)
      : ['Lubricantes', 'Repuestos Motor', 'Equipos de Seguridad', 'Filtros', 'Pinturas Marinas', 'Cables y Cadenas', 'Bombas', 'Válvulas', 'Herramientas', 'Electricidad'];

    const rfqs: RFQ[] = [];

    // RFQ 1: Draft - Lubricantes
    rfqs.push({
      id: crypto.randomUUID(),
      number: 'RFQ-001',
      title: `Compulsa ${categoryNames[0] || 'Lubricantes'} - Flota Remolcadores`,
      status: 'draft',
      items: [
        { id: crypto.randomUUID(), category: categoryNames[0] || 'Lubricantes', description: 'Aceite motor marino SAE 40', quantity: 500, unit: 'Litros', specs: 'Viscosidad SAE 40, apto motores marinos diesel' },
        { id: crypto.randomUUID(), category: categoryNames[0] || 'Lubricantes', description: 'Grasa multiuso marina', quantity: 50, unit: 'Kg', specs: 'Resistente al agua salada, punto de gota >180°C' },
      ],
      suppliers: [
        makeSupplier(supplierNames[0], false, false),
        makeSupplier(supplierNames[1], false, false),
        makeSupplier(supplierNames[2], false, false),
      ],
      responses: [],
      createdBy: 'Demo User',
      createdAt: '2026-04-10T09:00:00Z',
      deadline: '2026-04-25T23:59:59Z',
      notes: 'Pedido trimestral para toda la flota',
      mainCategory: categoryNames[0] || 'Lubricantes',
    });

    // RFQ 2: Draft - Equipos de Seguridad
    rfqs.push({
      id: crypto.randomUUID(),
      number: 'RFQ-002',
      title: `Compulsa ${categoryNames[2] || 'Equipos de Seguridad'} - Renovación Anual`,
      status: 'draft',
      items: [
        { id: crypto.randomUUID(), category: categoryNames[2] || 'Equipos de Seguridad', description: 'Chalecos salvavidas clase I', quantity: 60, unit: 'Unidades', specs: 'SOLAS aprobado, reflectante, con luz automática' },
        { id: crypto.randomUUID(), category: categoryNames[2] || 'Equipos de Seguridad', description: 'Aros salvavidas con rabiza', quantity: 30, unit: 'Unidades' },
        { id: crypto.randomUUID(), category: categoryNames[2] || 'Equipos de Seguridad', description: 'Extintores polvo ABC 10kg', quantity: 15, unit: 'Unidades', specs: 'Certificación IRAM, recarga incluida' },
      ],
      suppliers: [
        makeSupplier(supplierNames[3], false, false),
        makeSupplier(supplierNames[4], false, false),
      ],
      responses: [],
      createdBy: 'Demo User',
      createdAt: '2026-04-12T11:00:00Z',
      deadline: '2026-04-30T23:59:59Z',
      notes: 'Renovación obligatoria por normativa PNA',
      mainCategory: categoryNames[2] || 'Equipos de Seguridad',
    });

    // RFQ 3: Sent - Repuestos Motor
    rfqs.push({
      id: crypto.randomUUID(),
      number: 'RFQ-003',
      title: `Compulsa ${categoryNames[1] || 'Repuestos Motor'} - Mantenimiento Programado`,
      status: 'sent',
      items: [
        { id: crypto.randomUUID(), category: categoryNames[1] || 'Repuestos Motor', description: 'Filtros de aceite motor principal', quantity: 20, unit: 'Unidades', specs: 'Compatible Caterpillar C32/C18' },
        { id: crypto.randomUUID(), category: categoryNames[1] || 'Repuestos Motor', description: 'Correas de alternador', quantity: 10, unit: 'Unidades' },
        { id: crypto.randomUUID(), category: categoryNames[1] || 'Repuestos Motor', description: 'Kit juntas culata', quantity: 4, unit: 'Juegos', specs: 'Para CAT C32 ACERT' },
      ],
      suppliers: [
        makeSupplier(supplierNames[5], true, false),
        makeSupplier(supplierNames[6], true, false),
        makeSupplier(supplierNames[7], true, false),
        makeSupplier(supplierNames[8], true, false),
      ],
      responses: [],
      createdBy: 'Demo User',
      createdAt: '2026-03-28T14:00:00Z',
      sentAt: '2026-03-28T15:30:00Z',
      deadline: '2026-04-15T23:59:59Z',
      notes: 'Urgente para parada programada del remolcador Paraná',
      mainCategory: categoryNames[1] || 'Repuestos Motor',
    });

    // RFQ 4: Sent - Pinturas
    rfqs.push({
      id: crypto.randomUUID(),
      number: 'RFQ-004',
      title: `Compulsa ${categoryNames[4] || 'Pinturas Marinas'} - Carenado Q2`,
      status: 'sent',
      items: [
        { id: crypto.randomUUID(), category: categoryNames[4] || 'Pinturas Marinas', description: 'Pintura antifouling bicomponente', quantity: 200, unit: 'Litros', specs: 'Color negro, cobre >40%, para casco' },
        { id: crypto.randomUUID(), category: categoryNames[4] || 'Pinturas Marinas', description: 'Primer epoxi anticorrosivo', quantity: 100, unit: 'Litros' },
      ],
      suppliers: [
        makeSupplier(supplierNames[9], true, false),
        makeSupplier(supplierNames[0], true, false),
        makeSupplier(supplierNames[2], true, false),
      ],
      responses: [],
      createdBy: 'Demo User',
      createdAt: '2026-04-01T08:00:00Z',
      sentAt: '2026-04-01T10:00:00Z',
      deadline: '2026-04-20T23:59:59Z',
      notes: 'Para carenado programado en astillero durante Q2',
      mainCategory: categoryNames[4] || 'Pinturas Marinas',
    });

    // RFQ 5: Responses - Filtros (con 3 respuestas)
    const rfq5Items: RFQItem[] = [
      { id: 'item-5-1', category: categoryNames[3] || 'Filtros', description: 'Filtros de combustible primario', quantity: 30, unit: 'Unidades', specs: 'Tipo Racor 2040, 10 micrones' },
      { id: 'item-5-2', category: categoryNames[3] || 'Filtros', description: 'Filtros de aire motor', quantity: 15, unit: 'Unidades', specs: 'Compatible motores Caterpillar C32' },
      { id: 'item-5-3', category: categoryNames[3] || 'Filtros', description: 'Filtros hidráulicos de retorno', quantity: 20, unit: 'Unidades' },
    ];

    rfqs.push({
      id: 'rfq-responses-1',
      number: 'RFQ-005',
      title: `Compulsa ${categoryNames[3] || 'Filtros'} - Stock Semestral`,
      status: 'responses',
      items: rfq5Items,
      suppliers: [
        { ...makeSupplier(supplierNames[0], true, true), respondedAt: '2026-03-18T11:00:00Z' },
        { ...makeSupplier(supplierNames[3], true, true), respondedAt: '2026-03-19T16:00:00Z' },
        { ...makeSupplier(supplierNames[6], true, true), respondedAt: '2026-03-20T09:30:00Z' },
      ],
      responses: [
        {
          id: crypto.randomUUID(),
          rfqId: 'rfq-responses-1',
          supplierId: `supplier-${supplierNames[0].toLowerCase().replace(/\s+/g, '-')}`,
          supplierName: supplierNames[0],
          items: [
            { rfqItemId: 'item-5-1', unitPrice: 45.00, totalPrice: 1350.00, brand: 'Racor/Parker', origin: 'USA', notes: 'Stock inmediato' },
            { rfqItemId: 'item-5-2', unitPrice: 120.00, totalPrice: 1800.00, brand: 'Caterpillar OEM', origin: 'USA' },
            { rfqItemId: 'item-5-3', unitPrice: 65.00, totalPrice: 1300.00, brand: 'Donaldson', origin: 'Brasil' },
          ],
          totalAmountUSD: 4450.00,
          deliveryDays: 15,
          paymentTerms: '30 días',
          warranty: '6 meses',
          notes: 'Disponibilidad inmediata para filtros de combustible. Filtros de aire demoran 10 días.',
          validUntil: '2026-05-15T23:59:59Z',
          submittedAt: '2026-03-18T11:00:00Z',
        },
        {
          id: crypto.randomUUID(),
          rfqId: 'rfq-responses-1',
          supplierId: `supplier-${supplierNames[3].toLowerCase().replace(/\s+/g, '-')}`,
          supplierName: supplierNames[3],
          items: [
            { rfqItemId: 'item-5-1', unitPrice: 42.50, totalPrice: 1275.00, brand: 'Racor/Parker', origin: 'USA', notes: 'Precio por volumen' },
            { rfqItemId: 'item-5-2', unitPrice: 135.00, totalPrice: 2025.00, brand: 'Caterpillar OEM', origin: 'USA' },
            { rfqItemId: 'item-5-3', unitPrice: 58.00, totalPrice: 1160.00, brand: 'Hydac', origin: 'Alemania' },
          ],
          totalAmountUSD: 4460.00,
          deliveryDays: 20,
          paymentTerms: '60 días',
          warranty: '12 meses',
          notes: 'Garantía extendida incluida. Posibilidad de contrato anual con descuento adicional del 5%.',
          validUntil: '2026-04-30T23:59:59Z',
          submittedAt: '2026-03-19T16:00:00Z',
        },
        {
          id: crypto.randomUUID(),
          rfqId: 'rfq-responses-1',
          supplierId: `supplier-${supplierNames[6].toLowerCase().replace(/\s+/g, '-')}`,
          supplierName: supplierNames[6],
          items: [
            { rfqItemId: 'item-5-1', unitPrice: 48.00, totalPrice: 1440.00, brand: 'Fleetguard', origin: 'China', notes: 'Alternativa compatible' },
            { rfqItemId: 'item-5-2', unitPrice: 110.00, totalPrice: 1650.00, brand: 'Mann+Hummel', origin: 'Alemania' },
            { rfqItemId: 'item-5-3', unitPrice: 70.00, totalPrice: 1400.00, brand: 'Parker', origin: 'USA' },
          ],
          totalAmountUSD: 4490.00,
          deliveryDays: 10,
          paymentTerms: 'Contado',
          warranty: '6 meses',
          validUntil: '2026-05-20T23:59:59Z',
          submittedAt: '2026-03-20T09:30:00Z',
        },
      ],
      createdBy: 'Demo User',
      createdAt: '2026-03-10T08:00:00Z',
      sentAt: '2026-03-10T09:00:00Z',
      deadline: '2026-03-25T23:59:59Z',
      notes: 'Stock semestral de filtros para toda la flota',
      mainCategory: categoryNames[3] || 'Filtros',
    });

    // RFQ 6: Responses - Cables y Cadenas (con 3 respuestas)
    const rfq6Items: RFQItem[] = [
      { id: 'item-6-1', category: categoryNames[5] || 'Cables y Cadenas', description: 'Cable de acero 6x19 diámetro 22mm', quantity: 500, unit: 'Metros', specs: 'Galvanizado, carga rotura min 30 ton' },
      { id: 'item-6-2', category: categoryNames[5] || 'Cables y Cadenas', description: 'Cadena grado 80 de 16mm', quantity: 100, unit: 'Metros' },
    ];

    rfqs.push({
      id: 'rfq-responses-2',
      number: 'RFQ-006',
      title: `Compulsa ${categoryNames[5] || 'Cables y Cadenas'} - Renovación Equipos de Remolque`,
      status: 'responses',
      items: rfq6Items,
      suppliers: [
        { ...makeSupplier(supplierNames[1], true, true), respondedAt: '2026-03-22T10:00:00Z' },
        { ...makeSupplier(supplierNames[4], true, true), respondedAt: '2026-03-23T14:00:00Z' },
        { ...makeSupplier(supplierNames[8], true, true), respondedAt: '2026-03-24T11:00:00Z' },
      ],
      responses: [
        {
          id: crypto.randomUUID(),
          rfqId: 'rfq-responses-2',
          supplierId: `supplier-${supplierNames[1].toLowerCase().replace(/\s+/g, '-')}`,
          supplierName: supplierNames[1],
          items: [
            { rfqItemId: 'item-6-1', unitPrice: 18.50, totalPrice: 9250.00, brand: 'Bridon-Bekaert', origin: 'UK', notes: 'Certificado Lloyd\'s Register' },
            { rfqItemId: 'item-6-2', unitPrice: 42.00, totalPrice: 4200.00, brand: 'Gunnebo', origin: 'Suecia' },
          ],
          totalAmountUSD: 13450.00,
          deliveryDays: 25,
          paymentTerms: '30 días',
          warranty: '12 meses',
          notes: 'Incluye certificados de calidad y ensayo por lote. Flete incluido a Buenos Aires.',
          validUntil: '2026-05-10T23:59:59Z',
          submittedAt: '2026-03-22T10:00:00Z',
        },
        {
          id: crypto.randomUUID(),
          rfqId: 'rfq-responses-2',
          supplierId: `supplier-${supplierNames[4].toLowerCase().replace(/\s+/g, '-')}`,
          supplierName: supplierNames[4],
          items: [
            { rfqItemId: 'item-6-1', unitPrice: 16.80, totalPrice: 8400.00, brand: 'Wire Co', origin: 'USA' },
            { rfqItemId: 'item-6-2', unitPrice: 38.50, totalPrice: 3850.00, brand: 'Peerless', origin: 'USA' },
          ],
          totalAmountUSD: 12250.00,
          deliveryDays: 30,
          paymentTerms: '60 días',
          warranty: '6 meses',
          notes: 'Mejor precio del mercado. Importación directa sin intermediarios.',
          validUntil: '2026-04-30T23:59:59Z',
          submittedAt: '2026-03-23T14:00:00Z',
        },
        {
          id: crypto.randomUUID(),
          rfqId: 'rfq-responses-2',
          supplierId: `supplier-${supplierNames[8].toLowerCase().replace(/\s+/g, '-')}`,
          supplierName: supplierNames[8],
          items: [
            { rfqItemId: 'item-6-1', unitPrice: 20.00, totalPrice: 10000.00, brand: 'Redaelli', origin: 'Italia' },
            { rfqItemId: 'item-6-2', unitPrice: 45.00, totalPrice: 4500.00, brand: 'RUD', origin: 'Alemania' },
          ],
          totalAmountUSD: 14500.00,
          deliveryDays: 12,
          paymentTerms: 'Contado',
          warranty: '12 meses',
          notes: 'Entrega urgente disponible. Premium quality.',
          validUntil: '2026-05-15T23:59:59Z',
          submittedAt: '2026-03-24T11:00:00Z',
        },
      ],
      createdBy: 'Demo User',
      createdAt: '2026-03-15T10:00:00Z',
      sentAt: '2026-03-15T11:00:00Z',
      deadline: '2026-04-01T23:59:59Z',
      notes: 'Renovación obligatoria de equipos de remolque según inspección clasificadora',
      mainCategory: categoryNames[5] || 'Cables y Cadenas',
    });

    // RFQ 7: Closed - Bombas
    rfqs.push({
      id: crypto.randomUUID(),
      number: 'RFQ-007',
      title: `Compulsa ${categoryNames[6] || 'Bombas'} - Reemplazo Bombas Sentina`,
      status: 'closed',
      items: [
        { id: crypto.randomUUID(), category: categoryNames[6] || 'Bombas', description: 'Bomba de sentina autocebante', quantity: 4, unit: 'Unidades', specs: 'Caudal min 200 l/min, AISI 316, ATEX' },
      ],
      suppliers: [
        { ...makeSupplier(supplierNames[2], true, true), respondedAt: '2026-02-20T10:00:00Z' },
        { ...makeSupplier(supplierNames[5], true, true), respondedAt: '2026-02-22T14:00:00Z' },
      ],
      responses: [
        {
          id: crypto.randomUUID(),
          rfqId: '',
          supplierId: `supplier-${supplierNames[2].toLowerCase().replace(/\s+/g, '-')}`,
          supplierName: supplierNames[2],
          items: [
            { rfqItemId: '', unitPrice: 3500.00, totalPrice: 14000.00, brand: 'Grundfos', origin: 'Dinamarca' },
          ],
          totalAmountUSD: 14000.00,
          deliveryDays: 21,
          paymentTerms: '30 días',
          warranty: '24 meses',
          validUntil: '2026-03-15T23:59:59Z',
          submittedAt: '2026-02-20T10:00:00Z',
        },
        {
          id: crypto.randomUUID(),
          rfqId: '',
          supplierId: `supplier-${supplierNames[5].toLowerCase().replace(/\s+/g, '-')}`,
          supplierName: supplierNames[5],
          items: [
            { rfqItemId: '', unitPrice: 2800.00, totalPrice: 11200.00, brand: 'Desmi', origin: 'Dinamarca' },
          ],
          totalAmountUSD: 11200.00,
          deliveryDays: 28,
          paymentTerms: '60 días',
          warranty: '18 meses',
          notes: 'Incluye instalación y puesta en marcha',
          validUntil: '2026-03-20T23:59:59Z',
          submittedAt: '2026-02-22T14:00:00Z',
        },
      ],
      createdBy: 'Demo User',
      createdAt: '2026-02-10T08:00:00Z',
      sentAt: '2026-02-10T10:00:00Z',
      deadline: '2026-03-01T23:59:59Z',
      closedAt: '2026-03-05T16:00:00Z',
      notes: 'Cerrada - Se adjudicó a proveedor con mejor relación precio/garantía',
      mainCategory: categoryNames[6] || 'Bombas',
    });

    return rfqs;
  }
}

// Singleton instance
export const rfqService = new RFQService();
